<template>
  <v-dialog :model-value="true" content-class="popup popup--800 aScroll">
    <v-card v-if="validationResult.validation_result == 'incomplete'">
      <v-card-title>{{ t("dialog.address_validation.incomplete_address") }}</v-card-title>
      <v-card-text>
        <strong>{{ t("dialog.address_validation.entered_address") }}</strong>
        <address class="addressBlock mb-1">
          <strong>{{ t("form.common.street") }}: </strong>{{ originalAddress?.street_and_number }}<br>
          <strong>{{ t("form.common.postcode") }}: </strong>{{ originalAddress?.postal_code }}<br>
          <strong>{{ t("form.common.city") }}: </strong>{{ originalAddress?.city }}
        </address>

        {{ t("dialog.address_validation.missing", {items: listMissing(validationResult.missing_components)}) }}
      </v-card-text>
      <v-card-actions>
        <div class="buttonsWrapper">
          <v-btn size="large" class="mbtn mbtn--green" @click="$emit('continue')">
            {{ t("dialog.address_validation.im_sure") }}
          </v-btn>
          <v-btn size="large" class="mbtn mbtn--changeAddress" @click="$emit('cancel')">
            {{ t("dialog.address_validation.cancel") }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <v-card v-else-if="validationResult.validation_result == 'corrected'">
      <v-card-title>{{ t("dialog.address_validation.is_address_correct") }}</v-card-title>
      <v-card-text>
        {{ t("dialog.address_validation.not_address_verified") }}<br><br>
        <strong>{{ t("dialog.address_validation.entered_address") }}</strong>
        <address class="addressBlock mt-1 mb-3">
          <strong>{{ t("form.common.street") }}: </strong>{{ originalAddress?.street_and_number }}<br>
          <strong>{{ t("form.common.postcode") }}: </strong>{{ originalAddress?.postal_code }}<br>
          <strong>{{ t("form.common.city") }}: </strong>{{ originalAddress?.city }}
        </address>
        <strong>{{ t("dialog.address_validation.suggested_change") }}</strong>
        <address class="addressBlock mt-1">
          <strong>{{ t("form.common.street") }}: </strong><span :class="{'color-address-validation--blue': c!.route ? c!.route?.changed: c!.locality?.value }">{{
            c!.route ? c!.route?.value : c!.locality?.value
          }}</span>
          <span :class="{'color-address-validation--blue': c!.premise?.changed}">&nbsp;{{ c!.premise?.value }}</span>
          <br>
          <strong>{{ t("form.common.postcode") }}: </strong><span :class="{'color-address-validation--blue': c!.postal_code?.changed}">{{ c!.postal_code?.value }}</span><br>
          <strong>{{ t("form.common.city") }}: </strong><span :class="{'color-address-validation--blue': c!.route ? c!.locality?.changed : false}">{{ c!.locality?.value }}</span>
        </address>
      </v-card-text>
      <v-card-actions>
        <div class="buttonsWrapper" style="gap: 0.75rem 1.5rem;">
          <v-btn size="large" class="mbtn mbtn--blue" @click="useSuggestedAddress()">
            {{ t("dialog.address_validation.use_suggested") }}
          </v-btn>
          <v-btn size="large" class="mbtn mbtn--green" @click="$emit('continue')">
            {{ t("dialog.address_validation.use_original") }}
          </v-btn>
          <v-btn size="large" class="mbtn mbtn--changeAddress" @click="$emit('cancel')">
            {{ t("dialog.address_validation.cancel") }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <v-card v-else-if="validationResult.validation_result == 'suspicious'">
      <v-card-title>{{ t("dialog.address_validation.is_address_correct") }}</v-card-title>
      <v-card-text>
        {{ t("dialog.address_validation.not_address_verified") }}<br>
        <address class="addressBlock">
          <strong>{{ t("form.common.street") }}: </strong>
          <span
            :class="{'color-address-validation--red': c!.route?.changed || c!.route?.suspicious || c!.premise?.changed || c!.premise?.suspicious }"
          >
            {{ originalAddress?.street_and_number }}
          </span>
          <br>
          <strong>{{ t("form.common.postcode") }}: </strong>
          <span :class="{'color-address-validation--red': c!.postal_code?.changed || c!.postal_code?.suspicious}">
            {{ originalAddress?.postal_code }}
          </span>
          <br>
          <strong>{{ t("form.common.city") }}: </strong>
          <span :class="{'color-address-validation--red': c!.locality?.changed || c!.locality?.suspicious}">
            {{ originalAddress?.city }}
          </span>
        </address>
      </v-card-text>
      <v-card-actions>
        <div class="buttonsWrapper">
          <v-btn size="large" class="mbtn mbtn--green" @click="$emit('continue')">
            {{ t("dialog.address_validation.im_sure") }}
          </v-btn>
          <v-btn size="large" class="mbtn mbtn--changeAddress" @click="$emit('cancel')">
            {{ t("dialog.address_validation.cancel") }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script setup lang="ts">
const { localizeList } = useUtils();

// there are others, but we will probably never encounter them
type AddressComponentType =
  "street_address"
  | "route"
  | "country"
  | "locality"
  | "sublocality"
  | "premise"
  | "subpremise"
  | "postal_code";

type AddressComponentValue = {
  value: string,
  changed: boolean,
  suspicious: boolean
}
type AddressComponents = {
  route: AddressComponentValue,
  premise: AddressComponentValue,
  postal_code: AddressComponentValue,
  locality: AddressComponentValue
}

type IncompleteAddressResult = {
  validation_result: "incomplete",
  missing_components: AddressComponentType[]
}
type CorrectedAddressResult = {
  validation_result: "corrected",
  components: AddressComponents
}
type SuspiciousAddressResult = {
  validation_result: "suspicious",
  components: AddressComponents
}
export type AddressValidationResult = IncompleteAddressResult | CorrectedAddressResult | SuspiciousAddressResult;

const { t } = useI18n();

const props = defineProps<{
  validationResult: AddressValidationResult,
  originalAddress: ValidationAddress | null
}>();
const emit = defineEmits(["cancel", "continue", "continue-with-address"]);

// convenience
const c = computed<AddressComponents | null>(
  () => props.validationResult.validation_result === "incomplete" ? null : props.validationResult.components
);

function listMissing (missingComponents: AddressComponentType[]): string {
  return localizeList(missingComponents.map(comp => t(`dialog.address_validation.component.${comp}`)));
}

function useSuggestedAddress () {
  emit(
    "continue-with-address",
    {
      street_and_number: `${c.value?.route ? c.value?.route.value : c.value?.locality.value} ${c.value?.premise.value}`,
      postal_code: c.value?.postal_code.value ?? "",
      city: c.value?.locality.value ?? ""
    }
  );
}
</script>
